import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Button,
  Divider,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Avatar,
  Stack,
  CircularProgress,
  Paper,
  Grid,
  Fade,
  Tooltip,
  Badge,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from "@mui/material";
import { styled, ThemeProvider, createTheme } from "@mui/material/styles";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadForOfflineOutlinedIcon from "@mui/icons-material/DownloadForOfflineOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FileUploader from "../../components/input/new/fileUploader";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ChatModal from "../inbox/chatModal";
import { useSelector } from "react-redux";
import Header from "../components/header";
import { supabase } from "../../supabaseClient";
import { authenticatedFetch } from "../../network/api";

const primary = "#144a8c";
const primaryLight = "#2563eb";
const primaryDark = "#0f3660";
const success = "#10b981";
const warning = "#f59e0b";
const background = "#fafbfc";
const surfaceLight = "#ffffff";
const textPrimary = "#1f2937";
const textSecondary = "#6b7280";
const border = "#e5e7eb";

// Custom theme with Poppins font
const theme = createTheme({
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    subtitle1: {
      fontWeight: 600,
      letterSpacing: '-0.005em',
    },
    body1: {
      letterSpacing: '-0.005em',
    },
  },
  palette: {
    primary: {
      main: primary,
      light: primaryLight,
      dark: primaryDark,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
          transition: 'box-shadow 0.2s ease-in-out',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '-0.005em',
        },
      },
    },
  },
});

// Styled components
const GradientBackground = styled(Box)({
  minHeight: '100vh',
});

const HeaderCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${surfaceLight} 0%, ${primary}05 100%)`,
    border: `1px solid ${border}`,
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${primary} 0%, ${primaryLight} 100%)`,
    },
  }));



const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${border}`,
  background: surfaceLight,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px -8px rgba(20, 74, 140, 0.2)',
  },
}));

const TaskItem = styled(ListItem)(({ completed }) => ({
  borderRadius: 8,
  marginBottom: 8,
  border: `1px solid ${border}`,
  backgroundColor: completed ? `${success}08` : surfaceLight,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: completed ? `${success}12` : `${primary}05`,
    transform: 'translateX(4px)',
  },
}));

const FileItem = styled(Paper)(({ theme }) => ({
  padding: 16,
  borderRadius: 12,
  border: `1px solid ${border}`,
  backgroundColor: surfaceLight,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: `${primary}05`,
    borderColor: `${primary}30`,
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px -4px rgba(20, 74, 140, 0.15)',
  },
}));

const CountdownChip = styled(Chip)(({ urgency }) => {
  let bgColor = `${primary}15`;
  let textColor = primary;
  
  if (urgency === 'urgent') {
    bgColor = `${warning}15`;
    textColor = warning;
  } else if (urgency === 'now') {
    bgColor = `${success}15`;
    textColor = success;
  }
  
  return {
    backgroundColor: bgColor,
    color: textColor,
    fontWeight: 600,
    fontSize: '0.875rem',
    height: 32,
  };
});

const StatusChip = styled(Chip)(({ status }) => {
  const statusColors = {
    scheduled: { bg: `${primary}15`, color: primary },
    'in-progress': { bg: `${warning}15`, color: warning },
    completed: { bg: `${success}15`, color: success },
  };
  
  const colors = statusColors[status] || statusColors.scheduled;
  
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 600,
    textTransform: 'capitalize',
  };
});

export default function CasePage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const lawyer = useSelector((s) => s.attorneyData.attorneyData);
  const videoToken = useSelector((s) => s.attorneyData.videoToken);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    appointment: null,
    notes: "",
    files: [],
    tasks: [],
  });
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUser, setChatUser] = useState(null);
  const [intakeState, setIntakeState] = useState({ status: "loading", data: null });
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        // fetch appointment meta
        const apptRes = await authenticatedFetch(`/api/attorney/webapp/appointments/getById?appointmentId=${appointmentId}`);
        const appointment = apptRes.ok ? await apptRes.json() : null;
        // fetch case data (upsert empty on first load)
        const caseRes = await authenticatedFetch(`/api/attorney/webapp/case/get?appointmentId=${appointmentId}`);
        const payload = caseRes.ok ? await caseRes.json() : {};
        if (!mounted) return;
        setData({
          appointment,
          notes: payload.notes || "",
          files: Array.isArray(payload.files) ? payload.files : [],
          tasks: Array.isArray(payload.tasks) ? payload.tasks : [],
        });
        setError("");
      } catch (e) {
        console.error(e);
        setError("Failed to load case");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [appointmentId]);

  // Load intake status and data shape
  useEffect(() => {
    let active = true;
    (async () => {
      if (!appointmentId) return;
      try {
        setIntakeState({ status: "loading", data: null });
        const res = await authenticatedFetch(`/api/attorney/webapp/intake/forms/view?appointmentId=${appointmentId}`);
        if (!active) return;
        if (res.ok) {
          const { answers, urls, schema } = await res.json();
          setIntakeState({ status: "submitted", data: { answers, urls, schema } });
        } else if (res.status === 404) {
          setIntakeState({ status: "not_filled", data: null });
        } else {
          setIntakeState({ status: "error", data: null });
        }
      } catch (e) {
        if (!active) return;
        setIntakeState({ status: "error", data: null });
      }
    })();
    return () => {
      active = false;
    };
  }, [appointmentId]);

  // Realtime updates for intake status (scoped to this appointment)
  useEffect(() => {
    if (!appointmentId) return;
    const channel = supabase.channel(`intake_case_${appointmentId}`);
    channel
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "intake_responses", filter: `appointment_id=eq.${appointmentId}` },
        (payload) => {
          if (payload.new) {
            // Re-fetch view to get resolved URLs and schema snapshot
            authenticatedFetch(`/api/attorney/webapp/intake/forms/view?appointmentId=${appointmentId}`)
              .then((r) => (r.ok ? r.json() : null))
              .then((d) => {
                if (!d) return;
                setIntakeState({ status: "submitted", data: d });
              })
              .catch(() => {});
          }
        }
      )
      .subscribe();
    return () => {
      channel.unsubscribe();
    };
  }, [appointmentId]);

  const joinMeeting = () => {
    if (!data.appointment || !lawyer || !videoToken) return;
    const url = new URL("/lawyer-video", window.location.origin);
    url.searchParams.append("token", JSON.stringify(videoToken));
    url.searchParams.append("userId", JSON.stringify(lawyer.user_id));
    url.searchParams.append("callId", JSON.stringify(appointmentId));
    url.searchParams.append("name", JSON.stringify(lawyer.name));
    window.open(url, "_blank");
  };

  const openChat = () => {
    const c = data.appointment?.client;
    if (!c) return;
    setChatUser({
      ...c,
      name: c?.name || `${c?.first_name || ""} ${c?.last_name || ""}`.trim(),
    });
    setChatOpen(true);
  };

  const saveNotes = async (value) => {
    setSaving(true);
    try {
      setData((d) => ({ ...d, notes: value }));
      await authenticatedFetch(`/api/attorney/webapp/case/notes`, {
        method: "POST",
        body: JSON.stringify({ appointmentId, notes: value }),
      });
    } finally {
      setSaving(false);
    }
  };

  const addTask = async (title) => {
    if (!title) return;
    const optimistic = {
      id: `${Date.now()}`,
      title,
      done: false,
      createdAt: new Date().toISOString(),
    };
    setData((d) => ({ ...d, tasks: [optimistic, ...d.tasks] }));
    await authenticatedFetch(`/api/attorney/webapp/case/tasks`, {
      method: "POST",
      body: JSON.stringify({ appointmentId, action: "add", task: optimistic }),
    });
  };

  const toggleTask = async (taskId) => {
    setData((d) => ({
      ...d,
      tasks: d.tasks.map((t) => (t.id === taskId ? { ...t, done: !t.done } : t)),
    }));
    await authenticatedFetch(`/api/attorney/webapp/case/tasks`, {
      method: "POST",
      body: JSON.stringify({ appointmentId, action: "toggle", taskId }),
    });
  };

  const onFilesSelected = async (files) => {
    const uploadedUrls = [];
    for (const file of files) {
      const form = new FormData();
      form.append("user_id", data.appointment?.client_id || "");
      form.append("lawyer_id", data.appointment?.lawyer_id || "");
      form.append("documents[]", file);
      const resp = await authenticatedFetch(`/api/client/uploadAppointmentDocuments`, {
        method: "POST",
        body: form,
      });
      if (resp.ok) {
        const j = await resp.json();
        uploadedUrls.push(...(j.documents || []));
      }
    }

    if (uploadedUrls.length) {
      const newFiles = uploadedUrls.map((url) => ({
        id: `${Date.now()}_${Math.random()}`,
        name: url.split("/").pop(),
        url,
        uploadedAt: new Date().toISOString(),
      }));
      const next = [...newFiles, ...data.files];
      setData((d) => ({ ...d, files: next }));
      await authenticatedFetch(`/api/attorney/webapp/case/files`, {
        method: "POST",
        body: JSON.stringify({ appointmentId, files: newFiles }),
      });
    }
  };

  const countdown = useMemo(() => {
    if (!data.appointment?.requestedTimestamp || !data.appointment?.end_ts)
      return null;
    const start = new Date(data.appointment.requestedTimestamp);
    const end = new Date(data.appointment.end_ts);
    const now = new Date();
    if (now < start) return Math.max(0, start - now);
    if (now > end) return 0;
    return end - now;
  }, [data.appointment]);

  const getCountdownUrgency = (countdown) => {
    if (countdown === null) return 'normal';
    if (countdown === 0) return 'now';
    if (countdown < 900000) return 'urgent'; // Less than 15 minutes
    return 'normal';
  };

  const completedTasks = data.tasks.filter(t => t.done).length;
  const taskProgress = data.tasks.length > 0 ? (completedTasks / data.tasks.length) * 100 : 0;

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <GradientBackground>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh" }}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress size={40} thickness={4} />
              <Typography variant="h6" color="text.secondary">Loading case details...</Typography>
            </Stack>
          </Box>
        </GradientBackground>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
        <Header />
      <GradientBackground>
        <Box sx={{ maxWidth: 1400, mx: "auto", px: 3, pb: 4, pt: "100px" }}>
          {/* Top title + back button (separate from card) */}
          <Fade in timeout={300}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <Tooltip title="Go back">
                <IconButton 
                  onClick={() => navigate(-1)}
                  sx={{ 
                    bgcolor: surfaceLight, 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    '&:hover': { bgcolor: `${primary}08`, transform: 'translateX(-2px)' }
                  }}
                >
                  <ArrowBackIosNewRoundedIcon />
                </IconButton>
              </Tooltip>
              <Typography variant="h4" fontWeight={500} sx={{ color: textPrimary, fontSize: '24px' , fontFamily: 'Poppins' }}>
                Case Management
              </Typography>
            </Stack>
          </Fade>

          {/* Header Card with case context */}
          <Fade in timeout={500}>
            <HeaderCard sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} lg={8}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems={{ xs: "flex-start", sm: "center" }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: primary, 
                          width: 64, 
                          height: 64, 
                          fontSize: '1.5rem',
                          boxShadow: `0 4px 12px ${primary}30`
                        }}
                      >
                        {data.appointment?.client?.first_name?.[0]}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" fontWeight={700} sx={{ color: textPrimary, mb: 1 }}>
                          {data.appointment?.client?.first_name} {data.appointment?.client?.last_name}
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                          <Typography variant="body1" color="text.secondary">
                            {data.appointment?.appointmentType?.appointment_name || "Consultation"} • #{appointmentId}
                          </Typography>
                          <StatusChip 
                            label={data.appointment?.status || "scheduled"} 
                            status={data.appointment?.status || "scheduled"}
                            size="small"
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <Stack spacing={2} alignItems={{ xs: "flex-start", lg: "flex-end" }}>
                      {countdown !== null && (
                        <CountdownChip
                          icon={<AccessTimeIcon />}
                          label={countdown > 0 ? `${Math.floor(countdown / 86400000)}d ${Math.floor((countdown % 86400000) / 3600000)}h ${Math.floor((countdown % 3600000) / 60000)}m` : "Starting Now"}
                          urgency={getCountdownUrgency(countdown)}
                          variant="filled"
                        />
                      )}
                      <Button
                        variant="contained"
                        size="large"
                        onClick={joinMeeting}
                        startIcon={<PlayCircleOutlineRoundedIcon />}
                        sx={{
                          bgcolor: primary,

                          borderRadius: 2,
                          width: "200px",
                          px: "25px",
                          py: "10px",
                          boxShadow: `0 4px 12px ${primary}40`,
                          '&:hover': { 
                            bgcolor: primaryDark,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 6px 16px ${primary}40`,
                          },
                        }}
                      >
                        Join Video Call
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={openChat}
                        startIcon={<ChatBubbleOutlineRoundedIcon />}
                        sx={{
                          borderColor: primary,
                          color: primary,
                          width: "200px",
                          px: "25px",
                          py: "10px",
                          borderRadius: 2,
                          '&:hover': { borderColor: primaryDark, bgcolor: `${primary}08` },
                        }}
                      >
                        Open Chat
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </HeaderCard>
          </Fade>

          <Grid container spacing={3}>
            {/* Notes Section */}
            <Grid item xs={12} lg={6}>
              <Fade in timeout={700}>
                <StatsCard sx={{ height: '400px' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                      <Box sx={{ 
                        p: 1, 
                        borderRadius: 2, 
                        bgcolor: `${primary}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <NoteAltOutlinedIcon sx={{ color: primary, fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary }}>
                        Internal Notes
                      </Typography>
                      {saving && (
                        <Chip 
                          size="small" 
                          label="Saving..." 
                          sx={{ bgcolor: `${warning}15`, color: warning }}
                        />
                      )}
                    </Stack>
                    <TextField
                      fullWidth
                      placeholder="Add preparation notes, client background, key points to discuss..."
                      value={data.notes}
                      onChange={(e) => saveNotes(e.target.value)}
                      minRows={10}
                      maxRows={15}
                      multiline
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: background,
                          '&:hover fieldset': {
                            borderColor: `${primary}60`,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: primary,
                          },
                        },
                      }}
                    />
                  </CardContent>
                </StatsCard>
              </Fade>
            </Grid>

            {/* Tasks Section */}
            <Grid item xs={12} lg={6}>
              <Fade in timeout={900}>
                <StatsCard sx={{ height: '400px' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                      <Box sx={{ 
                        p: 1, 
                        borderRadius: 2, 
                        bgcolor: `${success}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CheckCircleOutlineRoundedIcon sx={{ color: success, fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary, flex: 1 }}>
                        Task Checklist
                      </Typography>
                      <Badge badgeContent={completedTasks} color="primary" max={99}>
                        <Chip 
                          label={`${completedTasks}/${data.tasks.length}`}
                          size="small"
                          sx={{ bgcolor: `${success}15`, color: success }}
                        />
                      </Badge>
                    </Stack>
                    
                    {data.tasks.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={taskProgress} 
                          sx={{ 
                            height: 6, 
                            borderRadius: 3,
                            bgcolor: `${success}15`,
                            '& .MuiLinearProgress-bar': {
                              bgcolor: success,
                            }
                          }} 
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {Math.round(taskProgress)}% Complete
                        </Typography>
                      </Box>
                    )}

                    <AddTask onAdd={addTask} />
                    
                    <Box sx={{ mt: 2, overflowY: 'auto' }}>
                      {data.tasks.map((task) => (
                        <TaskItem key={task.id} completed={task.done} disableGutters sx={{ paddingLeft: '25px'}}>
                          <ListItemIcon>
                            <Checkbox 
                              edge="start" 
                              checked={!!task.done} 
                              onChange={() => toggleTask(task.id)}
                              icon={<RadioButtonUncheckedIcon />}
                              checkedIcon={<CheckCircleIcon sx={{ color: success }} />}
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography 
                                sx={{ 
                                  textDecoration: task.done ? 'line-through' : 'none',
                                  color: task.done ? textSecondary : textPrimary,
                                  fontWeight: task.done ? 400 : 500,
                                }}
                              >
                                {task.title}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {new Date(task.createdAt).toLocaleString()}
                              </Typography>
                            }
                          />
                        </TaskItem>
                      ))}
                      {!data.tasks.length && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <CheckCircleOutlineRoundedIcon sx={{ fontSize: 48, color: textSecondary, mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No tasks yet. Add your first task above.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </StatsCard>
              </Fade>
            </Grid>

            {/* Documents Section */}
            <Grid item xs={12}>
              <Fade in timeout={1100}>
                <StatsCard>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Box sx={{ 
                        p: 1, 
                        borderRadius: 2, 
                        bgcolor: `${primaryLight}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <InsertDriveFileOutlinedIcon sx={{ color: primaryLight, fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary, flex: 1 }}>
                        Case Documents
                      </Typography>
                      <Chip 
                        label={`${data.files.length} files`}
                        size="small"
                        sx={{ bgcolor: `${primaryLight}15`, color: primaryLight }}
                      />
                    </Stack>
                    
                    <Box sx={{ mb: 3 }}>
                      <FileUploader 
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" 
                        multiple 
                        onFilesSelected={onFilesSelected} 
                      />
                    </Box>
                    
                    <Grid container spacing={2}>
                      {data.files.map((file) => (
                        <Grid item xs={12} sm={6} md={4} key={file.id}>
                          <FileItem elevation={0}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Box sx={{ 
                                p: 1, 
                                borderRadius: 2, 
                                bgcolor: `${primaryLight}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <CloudUploadOutlinedIcon sx={{ color: primaryLight }} />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight={600}
                                  sx={{ 
                                    color: textPrimary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {file.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(file.uploadedAt).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Tooltip title="Download">
                                <IconButton 
                                  size="small" 
                                  component="a" 
                                  href={file.url} 
                                  target="_blank" 
                                  rel="noreferrer"
                                  sx={{ 
                                    color: primary,
                                    '&:hover': { 
                                      bgcolor: `${primary}15`,
                                      transform: 'scale(1.1)',
                                    }
                                  }}
                                >
                                  <DownloadForOfflineOutlinedIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </FileItem>
                        </Grid>
                      ))}
                    </Grid>
                    
                    {!data.files.length && (
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 64, color: textSecondary, mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          No documents uploaded yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Upload case documents, contracts, or reference materials using the uploader above.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </StatsCard>
              </Fade>
            </Grid>

            {/* Intake Section */}
            <Grid item xs={12}>
              <Fade in timeout={1200}>
                <StatsCard>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                      <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <NoteAltOutlinedIcon sx={{ color: primary, fontSize: 22 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary, flex: 1 }}>
                        Client Intake
                      </Typography>
                      {intakeState.status === 'submitted' ? (
                        <Chip label="Submitted" size="small" sx={{ bgcolor: `#e8f5e9`, color: `#2e7d32`, fontWeight: 600 }} />
                      ) : intakeState.status === 'loading' ? (
                        <Chip label="Checking…" size="small" sx={{ bgcolor: `${primary}10`, color: primary, fontWeight: 600 }} />
                      ) : intakeState.status === 'not_filled' ? (
                        <Chip label="Not filled" size="small" sx={{ bgcolor: `#ffebee`, color: `#d32f2f`, fontWeight: 600 }} />
                      ) : (
                        <Chip label="Unavailable" size="small" sx={{ bgcolor: `#fff3e0`, color: `#f57c00`, fontWeight: 600 }} />
                      )}
                    </Stack>

                    <Box>
                      {intakeState.status === 'submitted' ? (
                        <Button
                          variant="contained"
                          onClick={() => setIntakeOpen(true)}
                          sx={{ bgcolor: primary, '&:hover': { bgcolor: primaryDark } }}
                        >
                          View Intake
                        </Button>
                      ) : (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                            {intakeState.status === 'loading' ? 'Loading intake status…' : 'Intake has not been filled yet.'}
                          </Typography>
                          <Button
                            variant="outlined"
                            disabled={resending}
                            onClick={async () => {
                              try {
                                setResending(true);
                                const r = await authenticatedFetch('/api/attorney/webapp/intake/forms/resend', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ appointmentId }),
                                });
                                if (r.status === 429) {
                                  const j = await r.json();
                                  alert(`Intake recently sent. Try again in ~${j.retryAfterMinutes} minutes.`);
                                } else if (r.status === 409) {
                                  setIntakeState((s) => ({ ...s, status: 'submitted' }));
                                } else if (!r.ok) {
                                  alert('Failed to resend intake.');
                                }
                              } finally {
                                setResending(false);
                              }
                            }}
                            sx={{ borderColor: primary, color: primary, '&:hover': { borderColor: primaryDark, bgcolor: `${primary}08` } }}
                          >
                            {resending ? 'Sending…' : 'Resend Intake'}
                          </Button>
                        </Stack>
                      )}
                    </Box>
                  </CardContent>
                </StatsCard>
              </Fade>
            </Grid>
          </Grid>
        </Box>
      </GradientBackground>
      {/* Intake Modal */}
      <Dialog open={intakeOpen} onClose={() => setIntakeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Intake Response</DialogTitle>
        <DialogContent dividers>
          {intakeState.status !== 'submitted' ? (
            <Typography variant="body2" color="text.secondary">No intake available.</Typography>
          ) : (
            intakeState.data?.schema?.fields?.map(({ id, label }) => {
              const answer = intakeState.data.answers[id];
              const url = intakeState.data.urls[id];
              return (
                <Box key={id} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {label}
                  </Typography>
                  {url ? (
                    <Link href={url} target="_blank" rel="noopener">
                      {answer?.filename || url}
                    </Link>
                  ) : (
                    <Typography>
                      {answer != null ? String(answer) : '—'}
                    </Typography>
                  )}
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIntakeOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <ChatModal open={chatOpen} onClose={() => setChatOpen(false)} clientUser={chatUser} />
    </ThemeProvider>
  );
}

function AddTask({ onAdd }) {
  const [value, setValue] = useState("");
  
  const handleAdd = () => {
    if (value.trim()) {
      onAdd(value.trim());
      setValue("");
    }
  };
  
  return (
    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Add a new task and press Enter..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleAdd();
          }
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: background,
            '&:hover fieldset': {
              borderColor: `${primary}60`,
            },
            '&.Mui-focused fieldset': {
              borderColor: primary,
            },
          },
        }}
      />
      <Button
        variant="contained"
        onClick={handleAdd}
        startIcon={<AddRoundedIcon />}
        sx={{
          bgcolor: primary,
          minWidth: 100,
          borderRadius: 2,
          '&:hover': { 
            bgcolor: primaryDark,
            transform: 'translateY(-1px)',
          },
        }}
      >
        Add
      </Button>
    </Stack>
  );
}